import { FlowSchema } from './flow-schema';
import { FlowItem, FlowItemType } from './flow-item';
import { FlowExecution, ExecutionState } from './flow-execution';
import { FlowExclusiveGateway } from './flow-gateway';
import { FlowEngineConfiguration } from './flow-engine-configuration';
import { FlowEvent, FlowEventType } from './flow-event';

/**
 * Create a new FlowEngine instance to control a process flow.
 */
export class FlowEngine {
    public readonly schema: FlowSchema;
    // public readonly items: FlowItem[];
    public readonly items: Map<string, FlowItem> = new Map();
    public readonly execution: FlowExecution = {
        state: ExecutionState.Idle,
        current: undefined,
        startNode: undefined
    };
    private configuration: FlowEngineConfiguration;

    private traverseHelper: FlowItem[][] = [];

    constructor(schema: FlowSchema, configuration: FlowEngineConfiguration) {
        this.schema = schema;
        const allItems: FlowItem[] = [
            ...schema.tasks,
            ...schema.sequenceFlow,
            ...schema.exclusiveGateways,
            ...schema.startEvents,
            ...schema.endEvents,
            ...schema.subProcess
        ];
        for (const item of allItems) {
            this.items.set(item.id, item);
        }

        this.configuration = configuration;
    }

    getState(): ExecutionState {
        return this.execution.state;
    }

    public start(): void {
        this.execution.state = ExecutionState.Running;
        if (this.configuration.startItemId) {
            this.execution.current = this.items.get(this.configuration.startItemId);
            if (this.configuration.itemCallback) this.configuration.itemCallback(this.execution.current, null);
        }
        let startId = (this.configuration.startItemId) ? this.configuration.startItemId : undefined;
        if (!startId && this.schema.startEvents.length === 1) {
            startId = this.schema.startEvents[0].id;
        }
        if (!startId) {
            this.execution.state = ExecutionState.Stopped;
            throw (new Error(`
            Start event not provided <=======
            Your schema must have either one start event or specify the startItemId at the configuration object.`));
        }
        const startNode = this.items.get(startId);
        if (startNode && startNode.type !== FlowItemType.Event
            && (startNode as FlowEvent).eventType !== FlowEventType.Start
        ) {
            throw (new Error(`
            Start item must be a FlowItem type: event. <=======
            You provided a startItemId that's associated with an event. Ensure the startItemId
            is an event and it has the EventType = start.`));
        }
        this.execution.startNode = startNode;
        this.execution.current = startNode;

        const disconnected = this.disconnectedNodes(this.execution.startNode);
        disconnected.map(disconnectedItem => {
            disconnectedItem.discarded = true;
            disconnectedItem.visited = false;
            if (this.configuration.discardedCallback) this.configuration.discardedCallback(disconnectedItem);
        });
    }

    public resume(itemId: string): void {
        this.execution.state = ExecutionState.Running;
        this.execution.current = this.items.get(itemId);
        console.log(this.execution.current);
        if (this.configuration.itemCallback) this.configuration.itemCallback(this.execution.current, null);
    }

    public nextStep(): FlowItem {
        // Next line returns string or array of strings depending on the flow.
        const outgoing = this.execution.current.outgoing;
        this.execution.current.visited = true;
        this.execution.current.discarded = false;
        // TODO: Obtener objeto modificadores del diagrama.
        // Los modificadores ejecutan un cambio de valor de otro elemento desde
        // la pregunta actual. Por ejemplo: AL pasar por el FlowItem actual,
        // vaya a la pregunta XXX y cambie su valor a undefined.
        // if (this.execution.current.id === 'tiene_techo') {
        //     this.executeModifiers(this.execution.current);
        // }
        let nextItem: FlowItem;
        if (outgoing) {
            if (Array.isArray(outgoing)) {
                // Convert current item in ExclusiveGateway
                const currentGateway: FlowExclusiveGateway = this.execution.current as FlowExclusiveGateway;
                const closureItem: FlowExclusiveGateway = this.schema.exclusiveGateways.find(
                    i => i.id === currentGateway.closingElementId
                );
                const nextItemId = this.configuration.gatewayCallback(currentGateway);
                nextItem = this.items.get(nextItemId);

                this.mapFlowItems(nextItem, closureItem, (item: FlowItem) => {
                    item.discarded = false;
                    if (this.configuration.activatedCallback) this.configuration.activatedCallback(item);
                });
                // Clean other gateway data.
                for (const itemId of outgoing) {
                    // Clean only those who does not belong to the next path
                    if (itemId !== nextItem.id) {
                        const clearItem: FlowItem = this.items.get(itemId);
                        this.mapFlowItems(clearItem, closureItem, (item: FlowItem) => {
                            item.visited = false;
                            item.discarded = true;
                            if (this.configuration.discardedCallback) this.configuration.discardedCallback(item);
                        });
                    }
                }
            } else {
                nextItem = this.items.get(outgoing);
            }
            const prevItem: FlowItem = this.execution.current;
            this.execution.current = nextItem;
            if (nextItem.type === FlowItemType.ExclusiveGateway && this.configuration.skipGateway) {
                return this.nextStep();
            }
            if (nextItem.type === FlowItemType.Sequence && this.configuration.skipSequence) {
                return this.nextStep();
            }
            if (this.configuration.itemCallback) this.configuration.itemCallback(nextItem, prevItem);
            return this.execution.current;
        } else {
            console.log('finished');
            this.execution.state = ExecutionState.Finished;
            if (this.configuration.finishedCallback) this.configuration.finishedCallback();
            return undefined;
        }
    }

    executeModifiers(item: FlowItem) {
        // TODO: Exec modifiers. This function might be deleted to execute a callback instead.
    }

    public prevStep(): FlowItem {
        // Next line returns string or array of strings.
        const incoming = this.execution.current.incoming;
        // TODO: Obtener objeto modificadores del diagrama

        let prevItem: FlowItem;
        if (incoming) {
            if (Array.isArray(incoming)) {
                const prevItems = incoming.map(iconmingId => this.items.get(iconmingId));
                prevItem = prevItems.find(i => (i.visited && !i.discarded));
            } else {
                prevItem = this.items.get(incoming);
            }
            const lastItem: FlowItem = this.execution.current;
            this.execution.current = prevItem;
            if (
                (
                    this.configuration.skipSequence &&
                    prevItem.type === FlowItemType.Sequence
                ) ||
                (
                    this.configuration.skipGateway &&
                    prevItem.type === FlowItemType.ExclusiveGateway
                )
            ) {
                return this.prevStep();
            }
            if (this.configuration.itemCallback) this.configuration.itemCallback(this.execution.current, lastItem);
            return this.execution.current;
        } else {
            return undefined;
        }
    }

    mapFlowItems(from: FlowItem, to: FlowItem, cb: (current: FlowItem) => void): void {
        let current: FlowItem = from;
        while (current !== to) {
            // console.log(`Executing cb for ${current.id}`);
            cb(current);
            if (Array.isArray(current.outgoing)) {
                const currentGateway: FlowExclusiveGateway = current as FlowExclusiveGateway;
                const closureItem: FlowExclusiveGateway = this.items.get(currentGateway.closingElementId) as FlowExclusiveGateway;
                for (const itemId of current.outgoing) {
                    // Clean only those who does not belong to the next path
                    const clearItem: FlowItem = this.items.get(itemId);
                    this.mapFlowItems(clearItem, closureItem, cb);
                }
                current = closureItem;
            } else {
                if (current.outgoing) {
                    current = this.items.get(current.outgoing);
                } else {
                    // If there is no outgoing item, then the map has reached an end event, so we need to stop the loop.
                    break;
                }
            }
        }
    }

    /**
     * Recursive function that runs all over the graph.
     * @param node The starting node to start calculating the paths
     * @param path used for recursive purposes. Use [] as initial value.
     */
    private traverse(node: FlowItem, path: FlowItem[], visited?: Set<FlowItem>) {
        if (!visited) {
            visited = new Set();
        }
        path.push(node);
        if ((node.type === FlowItemType.Event
            && (node as FlowEvent).eventType === FlowEventType.End) || visited.has(node)) {
            // console.log("Found Valid", path);
            this.traverseHelper.push(this.clone(path));
            return;
        }
        visited.add(node);
        if (Array.isArray(node.outgoing)) {
            for (const next of node.outgoing) {
                const newPath = this.clone(path);
                this.traverse(this.items.get(next), newPath, visited);
            }
        } else {
            const newPath = this.clone(path);
            this.traverse(this.items.get(node.outgoing), newPath, visited);
        }
    }

    /**
     * Returns all posible paths from one node to all End Events.
     * @param node The starting node to start calculating the paths
     */
    getPaths(node: FlowItem): FlowItem[][] {
        this.traverseHelper = [];
        this.traverse(node, []);
        return this.traverseHelper;
    }

    /**
     * Get all nodes that wont be accessible from this node.
     * @param node The starting node to begin the calculation from.
     */
    disconnectedNodes(node: FlowItem): FlowItem[] {
        // Get list of lists of FlowItem (or list of paths with FlowItem inside)
        const validPaths = this.getPaths(node);
        // Merge all flow items inside one array
        const merged = [].concat.apply([], validPaths);
        const connectedNodes = new Set<string>();
        for (const item of merged) {
            if (!connectedNodes.has(item.id)) {
                connectedNodes.add(item.id);
            }
        }
        return Array.from(this.items.values()).filter(x => !connectedNodes.has(x.id));
    }

    clone(A) {
        return JSON.parse(JSON.stringify(A));
    }

}
